import { normalise } from '@/utils/data/normalisers';
import { shortList } from '@/utils/formatters';
import { getProperShippingName } from '@/utils/substance/substanceHelpers';
import type { ImdgSubstance, RankedSubstance, SubstanceMatchBreakdown } from '@/api/types';

export interface VariantCandidate {
  id: string;
  substance: ImdgSubstance;
  score: number;
  breakdown?: SubstanceMatchBreakdown;
  meetsAutoSelectBar: boolean;
}

export type MatchReason = 'identical' | 'nearTie' | 'weak' | 'clear';

export type VariantField =
  'name' | 'cls' | 'pg' | 'lq' | 'ems' | 'stow' | 'packing' | 'tank' | 'selfReactive' | 'sp' | 'mp';

export interface VariantComparison {
  candidates: VariantCandidate[];
  reason: MatchReason;
  detail: string;
  differing: Set<VariantField>;
  lowerIds: Set<string>;
}

const NEAR_TIE_GAP = 5;
const TIE_EPSILON = 0.05;

/** `header` heads a table column; `label` names the field in a sentence. */
export const FIELDS: Record<VariantField, { header: string; label: string }> = {
  name: { header: 'Name', label: 'name' },
  cls: { header: 'Class', label: 'class' },
  pg: { header: 'PG', label: 'packing group' },
  lq: { header: 'LQ', label: 'limited quantity' },
  ems: { header: 'EmS', label: 'EmS' },
  stow: { header: 'Stowage', label: 'stowage' },
  packing: { header: 'Packing', label: 'packing instructions' },
  tank: { header: 'Tank/IBC', label: 'tank and IBC instructions' },
  selfReactive: { header: 'Self-reactive', label: 'self-reactive entries' },
  sp: { header: 'SP', label: 'special provisions' },
  mp: { header: 'MP', label: 'marine pollutant' },
};

export const OTHER_FIELDS: VariantField[] = ['tank', 'selfReactive', 'sp', 'mp'];

const join = (values: readonly string[] | undefined, sep = ', ') =>
  (values ?? []).filter(Boolean).join(sep);

const DEFAULT_DECK = 'On deck or under deck';

const schedule = (prefix: string, value: string | null | undefined) =>
  value ? (value.includes('-') ? value : `${prefix}-${value}`) : '';

const tankAndIbc = (s: ImdgSubstance) => [
  ...(s.tankInstructions ?? []),
  ...(s.ibcInstructions ?? []),
];

export function fieldValue(s: ImdgSubstance, field: VariantField): string {
  switch (field) {
    case 'name':
      return getProperShippingName(s) || '–';
    case 'cls': {
      const cls = join(s.class, ' + ') || '–';
      const sub = join(s.subsidiaryHazards);
      return sub ? `${cls} (${sub})` : cls;
    }
    case 'pg':
      return s.packingGroup && s.packingGroup !== 'None' ? s.packingGroup : '–';
    case 'lq':
      return s.limitedQuantity && s.limitedQuantity !== '0' ? s.limitedQuantity : '–';
    case 'ems':
      return (
        [
          schedule('F', s.emergencySchedules?.fire?.schedule),
          schedule('S', s.emergencySchedules?.spillage?.schedule),
        ]
          .filter(Boolean)
          .join(', ') || '–'
      );
    case 'stow': {
      const deck = s.stowage?.cargoShipStowageMessage;
      const note =
        deck && deck !== DEFAULT_DECK ? ` · ${deck.replace(/^On /, '').toLowerCase()}` : '';
      return `${s.stowage?.stowageCategory || '–'}${note}`;
    }
    case 'packing':
      return s.packingInstructions?.length ? shortList(s.packingInstructions) : '–';
    case 'tank': {
      const all = tankAndIbc(s);
      return all.length ? shortList(all) : 'None';
    }
    case 'selfReactive':
      return s.availableSelfReactiveDetails?.length
        ? String(s.availableSelfReactiveDetails.length)
        : 'None';
    case 'sp':
      return s.specialProvisions?.length ? shortList(s.specialProvisions, ' ') : '–';
    case 'mp':
      return s.isMarinePollutant ? 'Yes' : 'No';
  }
}

export const otherNote = (s: ImdgSubstance, field: VariantField) =>
  `${FIELDS[field].header} ${fieldValue(s, field)}`;

// Compare full lists, not the shortened display, so "P903, P908 +7" vs "P903, P908 +7" still differ.
function compareKey(s: ImdgSubstance, field: VariantField): string {
  switch (field) {
    case 'packing':
      return join(s.packingInstructions);
    case 'tank':
      return join(tankAndIbc(s));
    case 'sp':
      return join(s.specialProvisions);
    case 'selfReactive':
      return JSON.stringify(s.availableSelfReactiveDetails ?? []);
    default:
      return fieldValue(s, field);
  }
}

export const choiceTitle = (s: ImdgSubstance): string =>
  s.variantInformation?.description?.replace(/\.$/, '') ||
  getProperShippingName(s) ||
  s.substanceId ||
  '';

export interface ChoiceRef {
  n: number;
  title: string;
}

export const choiceRefs = (candidates: readonly VariantCandidate[]): Map<string, ChoiceRef> =>
  new Map(candidates.map((c, i) => [c.id, { n: i + 1, title: choiceTitle(c.substance) }]));

function reasonOf(
  candidates: VariantCandidate[],
  top: VariantCandidate[],
  differing: Set<VariantField>
): Pick<VariantComparison, 'reason' | 'detail'> {
  if (top.length > 1) {
    if (new Set(top.map((c) => getProperShippingName(c.substance).toUpperCase())).size > 1) {
      return {
        reason: 'nearTie',
        detail: `${top.length} choices score the same but are named differently`,
      };
    }
    const fields = [...differing].map((f) => FIELDS[f].label).join(', ');
    return {
      reason: 'identical',
      detail: fields
        ? `Same on every field the document gives. Differs by ${fields}`
        : 'Nothing separates these',
    };
  }
  if (candidates.length < 2) return { reason: 'clear', detail: 'Only choice' };
  const best = candidates[0].score;
  const gap = Math.round((best - candidates[1].score) * 10) / 10;
  if (gap < NEAR_TIE_GAP) return { reason: 'nearTie', detail: `Leads by ${gap} points out of 100` };
  if (!candidates[0].meetsAutoSelectBar) {
    return { reason: 'weak', detail: `Best is ${Math.round(best)}, too low to pick on its own` };
  }
  return { reason: 'clear', detail: `Leads by ${gap} points` };
}

export function compareVariants(ranked: readonly RankedSubstance[]): VariantComparison {
  const candidates = ranked
    .flatMap((r) =>
      r.substance?.substanceId
        ? [
            {
              id: r.substance.substanceId,
              substance: r.substance,
              score: r.matchScore ?? 0,
              breakdown: r.breakdown,
              meetsAutoSelectBar: !!r.meetsAutoSelectBar,
            },
          ]
        : []
    )
    .sort((a, b) => b.score - a.score);
  const best = candidates[0]?.score ?? 0;
  const top = candidates.filter((c) => Math.abs(c.score - best) < TIE_EPSILON);

  const differing = new Set(
    (Object.keys(FIELDS) as VariantField[]).filter(
      (f) => new Set(candidates.map((c) => compareKey(c.substance, f))).size > 1
    )
  );
  const { reason, detail } = reasonOf(candidates, top, differing);

  return {
    candidates,
    reason,
    detail,
    differing,
    lowerIds: new Set(top.length >= 2 ? candidates.slice(top.length).map((c) => c.id) : []),
  };
}

const words = (s: string): string[] => normalise.psnExtracted(s).split(' ').filter(Boolean);

const forms = (w: string) => [w, w.replace(/s$/, ''), w.replace(/es$/, ''), w.replace(/ies$/, 'y')];

export function markUnmatchedWords(
  name: string,
  against: string
): { text: string; unmatched: boolean }[] {
  const known = new Set(words(against).flatMap(forms));
  return name.split(/(\s+)/).map((text) => {
    const word = words(text)[0];
    return { text, unmatched: !!word && !forms(word).some((f) => known.has(f)) };
  });
}
